import { BlockType, CustomerType, PageType, RelationshipType } from '@common/entities';
import { ApplicationForm } from 'state/slices/leadProfileSlice';

import { AnyPage } from 'types/page';

interface Element {
  id: string;
  name: string;
  type: string;
  label?: string;
  customType?: string;
  elementType?: string;
  configuration?: {
    hideFromCoapplicant?: boolean;
  };
}

// Blocks to exclude for the lead profile.
const BLOCK_NAMES_TO_EXCLUDE = ['coapplicant_block', 'guarantors'];

const NON_LEASING_OCCUPANT_BLOCK_TYPES = [
  BlockType.PersonalInformation,
  BlockType.AddressCurrent,
  BlockType.AddressPrevious,
  BlockType.Income,
  BlockType.IdentificationType,
] as const;

interface ApplicantVisibilityPolicy {
  allowedBlockTypes?: readonly BlockType[];
  effectiveRelationshipType: RelationshipType;
  hideNonBlankPages: boolean;
}

const CHILD_OCCUPANT_POLICY: ApplicantVisibilityPolicy = {
  allowedBlockTypes: [BlockType.PersonalInformation],
  effectiveRelationshipType: RelationshipType.Other,
  hideNonBlankPages: true,
};

const NON_LEASING_OCCUPANT_POLICY: ApplicantVisibilityPolicy = {
  allowedBlockTypes: NON_LEASING_OCCUPANT_BLOCK_TYPES,
  effectiveRelationshipType: RelationshipType.Coapplicant,
  hideNonBlankPages: false,
};

const COAPPLICANT_FALLBACK_POLICY: ApplicantVisibilityPolicy = {
  effectiveRelationshipType: RelationshipType.Coapplicant,
  hideNonBlankPages: false,
};

const CUSTOMER_TYPE_VISIBILITY_POLICIES: Partial<Record<CustomerType, ApplicantVisibilityPolicy>> =
  {
    [CustomerType.NotResponsible]: CHILD_OCCUPANT_POLICY,
    [CustomerType.NonLeasingOccupant]: NON_LEASING_OCCUPANT_POLICY,
  };

function getApplicantVisibilityPolicy(
  relationshipType: RelationshipType,
  customerType?: CustomerType,
  isNonResponsibleOccupant = false,
): ApplicantVisibilityPolicy {
  const customerTypePolicy = customerType
    ? CUSTOMER_TYPE_VISIBILITY_POLICIES[customerType]
    : undefined;
  if (customerTypePolicy) {
    return customerTypePolicy;
  }

  if (isNonResponsibleOccupant) {
    return CHILD_OCCUPANT_POLICY;
  }

  if (relationshipType === RelationshipType.Other) {
    // Loading, failed, or unmapped relationships must not expose a full interview.
    // Personal information is the common subset for child and adult occupants.
    if (customerType === undefined) {
      return CHILD_OCCUPANT_POLICY;
    }
    return COAPPLICANT_FALLBACK_POLICY;
  }

  return {
    effectiveRelationshipType: relationshipType,
    hideNonBlankPages: false,
  };
}

/**
 * Whether a page is configured to be hidden from the given relationship type via the
 * page-level hide flags.
 */
function isPageHiddenForRelationship(page: AnyPage, relationshipType: RelationshipType): boolean {
  return Boolean(
    page.configuration &&
      ((page.configuration.hideFromCoApplicant &&
        relationshipType === RelationshipType.Coapplicant) ||
        (page.configuration.hideFromGuarantor && relationshipType === RelationshipType.Guarantor)),
  );
}

export function filterApplicationPages(
  applicationForm: Pick<ApplicationForm, 'pages'> | null,
  relationshipType: RelationshipType,
  hasDueNowCharges: boolean | undefined,
  isLeadProfile = false,
  isNonResponsibleOccupant = false,
  customerType?: CustomerType,
): AnyPage[] {
  if (!applicationForm || !applicationForm.pages) {
    return [];
  }

  const visibilityPolicy = getApplicantVisibilityPolicy(
    relationshipType,
    customerType,
    isNonResponsibleOccupant,
  );
  const { effectiveRelationshipType } = visibilityPolicy;

  // First, filter the pages based on the effective relationship type and page type.
  const filteredPages = applicationForm.pages.filter((page) => {
    // Floor Plans must remain completely hidden from secondary applicants,
    // regardless of page configuration or added content.
    const isSecondaryApplicant =
      effectiveRelationshipType === RelationshipType.Coapplicant ||
      effectiveRelationshipType === RelationshipType.Guarantor;
    if (page.type === PageType.FloorPlans && isSecondaryApplicant) {
      return false;
    }

    const shouldHide = isPageHiddenForRelationship(page, effectiveRelationshipType);

    // Keep Payment visible until charges have loaded; false means no payment is due.
    const shouldHidePayment =
      page.type === PageType.Payment && hasDueNowCharges === false && !isLeadProfile;

    return page.type !== PageType.ThankYou && !shouldHide && !shouldHidePayment;
  });

  // Now, filter out specific blocks within the sections of each page.
  // Only show the page if there are any elements left to show.
  const cleanedPages = filteredPages
    .map((page) => {
      if (page.type === PageType.Blank) {
        const newSections = page.sections
          .map((section: any) => {
            const newElements = section.elements.filter((element: Element) => {
              if (visibilityPolicy.allowedBlockTypes) {
                const isAllowedBlock = visibilityPolicy.allowedBlockTypes.includes(
                  element.customType as BlockType,
                );
                const hiddenFromCoApplicant =
                  effectiveRelationshipType === RelationshipType.Coapplicant &&
                  element.configuration?.hideFromCoapplicant;

                return isAllowedBlock && !hiddenFromCoApplicant;
              }
              if (isLeadProfile) {
                return !BLOCK_NAMES_TO_EXCLUDE.includes(element.name);
              }
              return true;
            });
            return { ...section, elements: newElements };
          })
          .filter((section: any) => section.elements.length > 0);

        if (newSections.length > 0) {
          return { ...page, sections: newSections };
        }
        return null;
      }

      // If it's a non-blank page (e.g. Floor Plans, Options) and the applicant is non-responsible, hide it
      if (visibilityPolicy.hideNonBlankPages) {
        return null;
      }

      return page;
    })
    .filter((page) => page !== null);

  // Finally, sort the pages by sequence.
  return cleanedPages.sort((a, b) => a.sequence - b.sequence) as AnyPage[];
}
